import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, ShieldCheck } from "lucide-react";
import { useLang, scrollToId } from "@/lang";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const { t } = useLang();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden spotlight">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.06),transparent_60%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-32 pb-20 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative z-10 w-full">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-tag"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
            className="text-xs uppercase tracking-[0.3em] font-mono text-amber-700/90 mb-8 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-amber-500/60 inline-block" />
            {t.hero?.tag}
          </motion.p>

          <h1 data-testid="hero-headline" className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] text-ivory">
            {t.hero?.lines?.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "italic text-champagne" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.45 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-sub"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
            className="mt-8 max-w-md text-base leading-relaxed text-zinc-600"
          >
            {t.hero?.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-booking-btn"
              onClick={() => scrollToId("booking")}
              className="group flex items-center gap-3 bg-champagne text-obsidian text-sm font-semibold uppercase tracking-[0.15em] rounded-full px-8 py-4 hover:bg-champagne-bright hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] transition-all duration-300"
            >
              {t.hero?.ctaPrimary}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-services-btn"
              onClick={() => scrollToId("services")}
              className="text-sm uppercase tracking-[0.15em] text-zinc-700 border border-amber-600/30 rounded-full px-8 py-4 hover:border-amber-600/70 hover:text-champagne transition-colors duration-300"
            >
              {t.hero?.ctaSecondary}
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-14 flex gap-10"
          >
            {t.hero?.stats?.map((s, i) => (
              <div key={i}>
                <div className="font-serif text-2xl sm:text-3xl text-champagne">{s.v}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative" ref={imgRef}>
          <motion.div
            data-testid="hero-brand-badge"
            initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
            className="absolute -top-8 -left-3 sm:-top-10 sm:-left-10 z-20 w-28 h-28 sm:w-36 sm:h-36 lg:w-44 lg:h-44 rounded-full bg-white border-2 border-amber-600/40 shadow-[0_20px_50px_rgba(0,0,0,0.18)] overflow-hidden"
          >
            <img src="/logo-badge.png" alt="Badge Kenny Cab — Taxis Parisiens" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.94, clipPath: "inset(12% 12% 12% 12%)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
            className="relative rounded-lg overflow-hidden border border-amber-500/20 shadow-[0_40px_80px_rgba(0,0,0,0.6)]"
          >
            <motion.img
              src="https://images.unsplash.com/photo-1597933794920-a313c09b8022?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxwYXJpcyUyMGVpZmZlbCUyMHRvd2VyJTIwbmlnaHR8ZW58MHx8fHwxNzg5OTAyMjI2fDA&ixlib=rb-4.1.0&q=85"
              alt="Paris by night — Tour Eiffel"
              data-testid="hero-image"
              style={{ y: imgY, scale: 1.15 }}
              className="w-full h-[420px] lg:h-[540px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            data-testid="hero-floating-badge"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: [0, -10, 0] }}
            transition={{
              opacity: { duration: 0.8, delay: 1.5 },
              y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.5 },
            }}
            className="absolute -bottom-6 -left-4 sm:-left-8 bg-surface/90 backdrop-blur-xl border border-amber-500/30 rounded-lg px-5 py-4 flex items-center gap-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
          >
            <span className="w-10 h-10 rounded-full bg-champagne/10 border border-amber-500/30 flex items-center justify-center text-champagne">
              <ShieldCheck size={18} strokeWidth={1.5} />
            </span>
            <div>
              <div className="text-sm font-semibold text-ivory flex items-center gap-2">
                {t.hero?.badgeTitle}
                <span className="status-dot w-2 h-2 rounded-full bg-emerald-400 inline-block" />
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">{t.hero?.badgeSub}</div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToId("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-600 hover:text-champagne transition-colors"
        aria-label="Scroll"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="block">
          <ArrowDown size={18} strokeWidth={1.5} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
